import { createContext, useCallback, useMemo, useState, type PropsWithChildren } from 'react'

import { useNavigate } from 'react-router'

import { useLogin, useLogout } from '@/features/user/hooks'
import type { TAuthContext, TCredenciaisLogin, TLoginErrorResponse } from '@/features/user/interfaces'
import { useLocalStorage } from '@/shared/hooks'
import type { IHttpResponse } from '@/shared/interfaces/http-client'
import type { TUsuarioLogado } from '@/shared/interfaces/user'

const AuthContext = createContext<TAuthContext>({
  user: null,
  state: { isAuthenticated: false, isLogando: false, error: null },
  reset: () => null,
  login: () => Promise.resolve({} as TUsuarioLogado),
  logout: () => Promise.resolve({} as IHttpResponse<{ message: string }>),
  setError: () => null,
})

const AuthProvider = ({ children }: PropsWithChildren) => {
  const [error, setError] = useState<TLoginErrorResponse | null>(null)
  const [user, setUser] = useLocalStorage<TUsuarioLogado | null>('user', null)
  const { mutateAsync, reset, isPending } = useLogin()
  const { mutateAsync: mutateAsyncLogout } = useLogout()
  const navigate = useNavigate()
  const isAuthenticated = Boolean(user?.usuarioLogadoId)

  const login = useCallback(
    async (credenciais: TCredenciaisLogin) =>
      await mutateAsync(credenciais, {
        onSuccess: (response) => {
          setUser(response)
          navigate('/', { replace: true })
        },
        onError: (_error) => {
          if (!_error?.code) return
          setError({
            descricao: _error?.response?.data.descricao ?? _error.code,
            titulo: _error?.response?.data.titulo ?? _error?.message,
          })
        },
      }),
    [mutateAsync, navigate, setUser],
  )

  const logout = useCallback(
    async () =>
      mutateAsyncLogout(undefined, {
        onSuccess: () => {
          setUser(null)
          navigate('/login', { replace: true })
        },
      }),
    [mutateAsyncLogout, navigate, setUser],
  )
  const contextValue = useMemo(
    () => ({
      user,
      login,
      logout,
      reset,
      setError: setError,
      state: {
        error: error,
        isLogando: isPending,
        isAuthenticated,
      },
    }),
    [user, login, logout, reset, error, isPending, isAuthenticated],
  )

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export { AuthContext, AuthProvider }
